import { useEffect, useState } from "react";
import { http, apiError } from "@/lib/api";
import { Gift, Link as LinkIcon, Copy, Check, Users, Clock, Trophy, ShareNetwork } from "@phosphor-icons/react";

export default function ReferralsPage() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await http.get("/referrals/me");
        setData(data);
      } catch (e) {
        setErr(apiError(e));
      }
    })();
  }, []);

  const copyLink = async () => {
    if (!data?.share_url) return;
    try {
      await navigator.clipboard.writeText(data.share_url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard blocked — non-fatal */
    }
  };

  const shareNative = async () => {
    if (!data?.share_url) return;
    if (navigator.share) {
      try {
        await navigator.share({
          title: "Lazy Lean Method",
          text: "The 7 Lazy Lean Habits™. First 10 kg gone. No diet. Use my link:",
          url: data.share_url,
        });
      } catch {
        /* user cancelled */
      }
    } else {
      copyLink();
    }
  };

  if (!data && !err) {
    return <div className="p-6 md:p-10 max-w-4xl mx-auto"><div className="id-label text-zinc-500 id-pulse">— Loading —</div></div>;
  }

  return (
    <div className="p-6 md:p-10 max-w-4xl mx-auto" data-testid="referrals-page">
      <div className="id-label mb-3">— Refer & Earn —</div>
      <h1 className="id-h1 mb-2">A week free for every mate.</h1>
      <p className="text-zinc-400 font-manrope mb-10 max-w-2xl">
        Share your link. When a mate signs up and completes their first week of the 12-Week Transformation, you get <span className="text-[#FF3333] font-semibold">$79 AUD credited</span> to your next weekly debit. No cap. No expiry.
      </p>

      {err && <div className="border border-[#FF3333] bg-[#FF3333]/10 px-4 py-3 text-sm text-[#FF3333] mb-6" data-testid="referrals-error">{err}</div>}

      {data && (
        <>
          {/* Share card */}
          <div className="id-card mb-6" data-testid="referral-share-card">
            <div className="flex items-center gap-2 id-label mb-4">
              <Gift size={14} weight="bold" className="text-[#FF3333]" /> Your share link
            </div>

            <div className="border border-[#262626] bg-[#0F0F0F] px-4 py-3 flex items-center justify-between gap-3 mb-4 overflow-hidden">
              <code className="text-sm font-manrope text-zinc-200 truncate" data-testid="referral-link">{data.share_url}</code>
              <button
                type="button"
                onClick={copyLink}
                className="shrink-0 px-3 py-1.5 border border-[#FF3333] text-[#FF3333] hover:bg-[#FF3333] hover:text-white transition-colors uppercase font-oswald text-xs tracking-[0.15em] flex items-center gap-1.5"
                data-testid="referral-copy-button"
              >
                {copied ? <Check size={12} weight="bold" /> : <Copy size={12} weight="bold" />}
                {copied ? "Copied" : "Copy"}
              </button>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <div className="text-xs text-zinc-500 font-manrope">
                Or share your code: <span className="text-white font-bold tracking-widest">{data.code}</span>
              </div>
              <button
                type="button"
                onClick={shareNative}
                className="ml-auto id-btn-primary text-xs px-4 py-2 flex items-center gap-2"
                data-testid="referral-share-button"
              >
                <ShareNetwork size={14} weight="bold" /> Share
              </button>
            </div>
          </div>

          {/* Stats grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="referral-stats">
            <Stat label="Total referred" value={data.total_referred} icon={Users} testid="stat-total" />
            <Stat label="Awaiting payment" value={data.pending} icon={Clock} subtle testid="stat-pending" />
            <Stat label="Weeks earned" value={data.weeks_earned} icon={Trophy} accent testid="stat-earned" />
            <Stat label="Pending credits" value={data.pending_credits} icon={Gift} testid="stat-pending-credits" />
          </div>

          {/* How it works */}
          <div className="id-card mb-6">
            <div className="id-label mb-4 text-zinc-400">How it works</div>
            <ol className="space-y-3">
              {[
                "Share your link or code with a mate.",
                "They sign up, complete onboarding, and pay their first $79.",
                "Your next weekly debit is automatically credited $79 (free week). It's actually free.",
                "No cap — refer 4 mates, get a whole month off.",
              ].map((step, i) => (
                <li key={i} className="flex gap-3 items-start">
                  <span className="font-oswald text-xl text-[#FF3333] font-bold leading-none">0{i + 1}</span>
                  <span className="text-sm text-zinc-300 font-manrope leading-relaxed pt-0.5">{step}</span>
                </li>
              ))}
            </ol>
          </div>

          {/* Recent referrals */}
          {data.recent && data.recent.length > 0 && (
            <div className="id-card" data-testid="referral-recent">
              <div className="id-label mb-4 text-zinc-400">Your referrals</div>
              <div className="space-y-2">
                {data.recent.map((r, i) => (
                  <div key={i} className="flex items-center justify-between border-b border-[#1F1F1F] pb-2 last:border-0 last:pb-0">
                    <div className="font-manrope text-sm text-zinc-200">{r.name || "—"}</div>
                    <div>
                      {r.status === "credited" && (
                        <span className="text-xs px-2 py-1 border border-[#FF3333] bg-[#FF3333]/10 text-[#FF3333] uppercase tracking-[0.15em] font-bold">Credited</span>
                      )}
                      {r.status === "qualified" && (
                        <span className="text-xs px-2 py-1 border border-emerald-700 bg-emerald-700/10 text-emerald-400 uppercase tracking-[0.15em] font-bold">Qualified</span>
                      )}
                      {r.status === "pending" && (
                        <span className="text-xs px-2 py-1 border border-zinc-700 bg-zinc-800 text-zinc-400 uppercase tracking-[0.15em] font-bold">Pending</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function Stat({ label, value, icon: Icon, accent, subtle, testid }) {
  return (
    <div className={`border ${accent ? "border-[#FF3333]" : "border-[#262626]"} bg-[#0F0F0F] px-4 py-3`} data-testid={testid}>
      <div className="flex items-center justify-between mb-1">
        <div className="text-[10px] text-zinc-500 uppercase tracking-[0.15em] font-bold">{label}</div>
        <Icon size={14} weight="bold" className={accent ? "text-[#FF3333]" : "text-zinc-500"} />
      </div>
      <div className={`font-oswald text-3xl font-bold ${accent ? "text-[#FF3333]" : subtle ? "text-zinc-400" : "text-white"}`}>{value}</div>
    </div>
  );
}
